import { memo, useContext, useState, useEffect } from 'react'

// Material UI
import {
  InputLabel,
  Stack,
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogTitle,
  DialogContent,
  useTheme,
  IconButton,
  Typography,
  FormControlLabel,
  Checkbox
} from '@mui/material'

import { Close } from '@mui/icons-material'
import useMediaQuery from '@mui/material/useMediaQuery'
import { updateGalerias } from '../services/galerias.services'
import ValidarDireccion from '../services/validacionesDir'

// form validation
import * as yup from 'yup'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'

import { GaleriasContext, IDimension, IGalerias } from '../context/galerias.context'
import SnackBarUtils from '../../../utils/SnackBarUtils'
import { TenantsContext } from '../../../context/tenants.context'
import { CreaGaleriaProps, SelectedDim } from './creaGalerias'

const MAX_NOMBRE_LENGTH = 75
const MAX_DIR_LENGTH = 150
const MAX_DIM_LENGTH = 50

interface EditGaleriaProps extends CreaGaleriaProps {
  editGaleriaCode: any
  setEliminaGaleria: (elimina: boolean) => void
}

const EditGaleria = ({ open, setOpen, editGaleriaCode, setEliminaGaleria, dimensiones }: EditGaleriaProps) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const [center, setCenter] = useState<string>('')
  const [loading, setLoading] = useState(false)
  const [latitud, setLatitud] = useState<number>(0)
  const [longitud, setLongitud] = useState<number>(0)
  const [direccionValidada, setDireccionValidada] = useState<boolean>(true)
  const [selectedDim, setSelectedDim] = useState<SelectedDim[]>([])
  const [errDims, setErrDims] = useState<{ id: number; error: string }[]>(null)
  const { galerias, fetchData } = useContext(GaleriasContext)
  const { tenant } = useContext(TenantsContext)

  // PRODUCT SAVE
  const [editGaleria, setEditGaleria] = useState<any>(null)

  useEffect(() => {
    if (editGaleriaCode && open) {
      const galeriaEditada = galerias.find((galeria) => galeria.codGaleria === editGaleriaCode)
      setEditGaleria(galeriaEditada)
    }
  }, [editGaleriaCode, open])

  const schema: yup.AnyObjectSchema = yup.object({
    nombreGaleria: yup.string().required('El nombre es obligatorio'),
    direcGaleria: yup.string().required('La direccion es obligatoria')
  })

  const {
    register,
    handleSubmit,
    reset,
    getValues,
    setValue,
    formState: { errors },
    setError,
    clearErrors
  } = useForm({
    resolver: yupResolver(schema),
    reValidateMode: 'onBlur'
  })

  const validarDimensiones = (): boolean => {
    const errores: Array<{ id: number; error: string }> = []

    selectedDim.forEach((dim) => {
      if (!dim.activo) return

      if (!dim.valorDefault?.trim()) {
        errores.push({ id: dim.id, error: 'Debes agregar un valor' })
        return
      }

      const dimension = dimensiones.find((d: IDimension) => d.idGaleriaEstructuraDimension === dim.id)

      if (dimension?.tipoDato === 'int') {
        if (isNaN(Number(dim.valorDefault)) || !Number.isInteger(Number(dim.valorDefault))) {
          errores.push({ id: dim.id, error: 'El valor debe ser un número entero' })
        }
      }
    })

    setErrDims(errores)
    return Object.keys(errores).length === 0
  }

  const onSubmit = async (data: any) => {
    if (!direccionValidada) {
      setError('direcGaleria', {
        message: 'Debes validar la dirección',
        type: 'manual'
      })
      return SnackBarUtils.error('Debes validar la dirección ingresada')
    }

    if (errDims?.length > 0) return

    const updatedGaleria: IGalerias = {
      codGaleria: editGaleria.codGaleria,
      nombre: editGaleria.nombre,
      direccion: editGaleria.direccion,
      latitud: latitud,
      longitud: longitud,
      estructura: selectedDim
        .filter((dim: SelectedDim) => dim.activo)
        .map((dim: SelectedDim) => ({
          idGaleriaEstructuraDimension: dim.id,
          valorDefault: dim.valorDefault,
          obligatorio: dim.obligatorio
        }))
    }

    try {
      setLoading(true)
      await updateGalerias(updatedGaleria, tenant)
      await fetchData()
      SnackBarUtils.success('Galería actualizada correctamente')
    } catch (e) {
      console.log(e)
      SnackBarUtils.error('Error al actualizar la galería')
    } finally {
      setLoading(false)
      setOpen(false)
      setErrDims(null)
    }
  }

  useEffect(() => {
    const selected: SelectedDim[] = dimensiones.map((dim: IDimension) => {
      const estructuraItem = editGaleria?.estructura?.find(
        (est) => est.idGaleriaEstructuraDimension === dim.idGaleriaEstructuraDimension
      )

      return {
        activo: estructuraItem ? true : dim.usarPorDefault,
        id: dim.idGaleriaEstructuraDimension,
        nombre: dim.nombre,
        valorDefault: estructuraItem?.valorDefault || '',
        obligatorio: estructuraItem?.obligatorio || false
      }
    })

    // Ordenar: primero los activos (true), luego los inactivos (false)
    selected.sort((a, b) => (a.activo === b.activo ? 0 : a.activo ? -1 : 1))

    setSelectedDim(selected)
  }, [dimensiones, editGaleria])

  const handleClose = async () => {
    setOpen(false)
    setErrDims(null)
    setSelectedDim([])
    setCenter('')
    setDireccionValidada(false)
    setLatitud(0)
    setLongitud(0)
    reset()
  }

  //if (loading) return <></>;
  return (
    <>
      <Dialog maxWidth={isMobile ? 'lg' : 'sm'} fullWidth fullScreen={isMobile} open={open} onClose={handleClose}>
        {editGaleria && (
          <form onSubmit={handleSubmit(onSubmit)} style={{ overflowX: 'hidden' }}>
            <DialogTitle
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
            >
              Edición de la Galería
              {open ? (
                <IconButton
                  aria-label="close"
                  onClick={handleClose}
                  sx={{
                    color: (theme) => theme.palette.grey[500]
                  }}
                >
                  <Close />
                </IconButton>
              ) : null}
            </DialogTitle>
            <DialogContent>
              <Stack direction={'column'} sx={{ margin: '20px 0' }} spacing="20px">
                <Stack sx={{ width: '100%' }}>
                  <InputLabel
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      width: '100%'
                    }}
                  >
                    Nombre de la Galería
                    <Typography variant="subtitle2">
                      {editGaleria.nombre.length || 0}/{MAX_NOMBRE_LENGTH}
                    </Typography>
                  </InputLabel>
                  <TextField
                    /* disabled={loading} */
                    sx={{ width: '100%' }}
                    error={!!errors.nombreGaleria}
                    helperText={errors?.nombreGaleria && String(errors?.nombreGaleria?.message)}
                    variant="outlined"
                    {...register('nombreGaleria')}
                    onChange={(e) => {
                      setEditGaleria({
                        ...editGaleria,
                        nombre: e.target.value
                      })
                    }}
                    inputProps={{
                      maxLength: MAX_NOMBRE_LENGTH
                    }}
                    value={editGaleria.nombre}
                  />
                </Stack>
                <ValidarDireccion
                  loading={loading}
                  register={register}
                  setValue={setValue}
                  getValues={getValues}
                  errors={errors}
                  center={center}
                  setCenter={setCenter}
                  setError={setError}
                  clearErrors={clearErrors}
                  setLongitud={setLongitud}
                  setLatitud={setLatitud}
                  editGaleria={editGaleria}
                  setEditGaleria={setEditGaleria}
                  setDireccionValidada={setDireccionValidada}
                />
              </Stack>
              {selectedDim.map((dim: SelectedDim, i: number) => {
                const err = errDims?.find((e) => e.id === dim.id)

                return (
                  <>
                    <InputLabel
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '2rem',
                        width: '100%',
                        marginTop: '1rem'
                      }}
                    >
                      {dim.nombre}
                      <Typography variant="subtitle2">
                        {dim.valorDefault?.length || 0}/{MAX_DIM_LENGTH}
                      </Typography>
                    </InputLabel>
                    <Stack
                      key={i}
                      direction="row"
                      spacing={2}
                      alignItems="center"
                      sx={{ width: '100%', marginTop: '1rem' }}
                    >
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={dim.activo}
                            onChange={(e) => {
                              const newSelected = [...selectedDim]
                              newSelected[i].activo = e.target.checked
                              setSelectedDim(newSelected)
                            }}
                            disabled={loading}
                          />
                        }
                        label="Activo"
                      />

                      <TextField
                        value={dim.valorDefault}
                        onChange={(e) => {
                          const newSelected = [...selectedDim]
                          newSelected[i].valorDefault = e.target.value
                          setSelectedDim(newSelected)
                        }}
                        onBlur={validarDimensiones}
                        error={!!err}
                        helperText={dim.activo && (err?.error || '')}
                        placeholder="Valor default"
                        variant="outlined"
                        disabled={loading || !dim.activo}
                        sx={{ flexGrow: 1 }}
                        inputProps={{ maxLength: MAX_DIM_LENGTH }}
                      />

                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={dim.obligatorio}
                            onChange={(e) => {
                              const newSelected = [...selectedDim]
                              newSelected[i].obligatorio = e.target.checked
                              setSelectedDim(newSelected)
                            }}
                            disabled={loading || !dim.activo}
                          />
                        }
                        label="Obligatorio"
                      />
                    </Stack>
                  </>
                )
              })}
            </DialogContent>

            <DialogActions
              sx={{
                diplay: 'flex',
                alignItems: 'center',
                alignSelf: 'flex-end',
                justifyContent: 'space-between',
                width: '100%',
                padding: '22px',
                paddingTop: 0,
                gap: '8px',
                position: 'relative'
              }}
            >
              <Button color="inherit" onClick={handleClose}>
                Volver
              </Button>
              <Stack direction="row" spacing={1}>
                <Button
                  variant="contained"
                  /* disabled={loading} */
                  color="error"
                  onClick={() => setEliminaGaleria(true)}
                >
                  Eliminar
                </Button>
                <Button variant="contained" type="submit">
                  Modificar
                </Button>
              </Stack>
            </DialogActions>
          </form>
        )}
      </Dialog>
    </>
  )
}

export default memo(EditGaleria)
